import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { PropertyPermission } from 'src/modules/shared/types/permissions.type';
import type { User } from 'src/api/generated';
import React from 'react';
import type { CatalogProperty } from 'src/api/generated';
import { Text } from 'src/modules/shared/components/atoms/text';
import {
  TagDropdown,
  type TagDropdownOption,
} from 'src/modules/shared/components/molecules/dropdowns/tag-dropdown';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';

type TranslateFn = (
  key: TranslationKey,
  params?: Record<string, string>
) => string;

interface PropertyAssigneeTagDropdownProps {
  property: CatalogProperty;
  users: User[];
  usersById: Map<number, User>;
  usersLoading: boolean;
  isAssigning: boolean;
  onUserAssign: (propertyId: number, userId: number | null) => void;
  t: TranslateFn;
}

export const PropertyAssigneeTagDropdown: React.FC<
  PropertyAssigneeTagDropdownProps
> = ({
  property,
  users,
  usersById,
  usersLoading,
  isAssigning,
  onUserAssign,
  t,
}) => {
  const { hasPermission: canReassign } = useHasPermissions(
    PropertyPermission.Update
  );
  const userId = property.userId ?? null;
  const currentUser = typeof userId === 'number' ? usersById.get(userId) : null;

  const userOptions: TagDropdownOption[] = [
    {
      id: 'unassign',
      content: (
        <Text size="small" color="secondary">
          {t('catalog.properties.assigned.unassigned')}
        </Text>
      ),
      selected: userId == null,
    },
    ...users.map((user) => ({
      id: user.id,
      content: (
        <Text size="small" color="secondary">
          {formatFullName(user.firstName, user.lastName, user.email)}
        </Text>
      ),
      selected: user.id === userId,
    })),
  ];

  const label = currentUser
    ? formatFullName(
        currentUser.firstName,
        currentUser.lastName,
        currentUser.email
      )
    : userId == null
      ? t('catalog.properties.assigned.unassigned')
      : `#${userId}`;
  if (!canReassign) {
    return (
      <Text size="tiny" color="secondary">
        {label}
      </Text>
    );
  }

  return (
    <TagDropdown
      content={label}
      variant="primary"
      appearance="outline"
      size="2xs"
      options={userOptions}
      isLoading={usersLoading || isAssigning}
      onOptionSelect={(option) => {
        const nextUserId = option.id === 'unassign' ? null : Number(option.id);
        onUserAssign(property.id, nextUserId);
      }}
    />
  );
};
